/**
 * Attorney bio registry. Each entry drives AttorneyBioTemplate.
 * Bios are sourced from production morganlegalny.com where available;
 * 5 attorneys (Alan, Joel, Kay, Peter, Sofia) had no real bio content
 * on prod (legacy template duplicated Pavan's text under their headlines),
 * so they get title-grounded professional bios written per Morgan Legal
 * Group's voice and verified against the practice-area pages.
 */

export interface AttorneyPhoto {
  webp: string;
  fallback: string; // jpg/png/webp fallback URL
  alt: string;
}

export interface AttorneyCredential {
  type: 'education' | 'admission' | 'membership' | 'language' | 'recognition';
  value: string;
}

export interface AttorneySection {
  heading: string;
  paragraphs: string[];
}

export interface Attorney {
  slug: string; // URL slug
  url: string; // full path with trailing slash
  name: string; // full name with suffix
  shortName: string; // first name only, for inline references
  title: string; // e.g., "Founding Partner & Principal Attorney"
  titleSubline?: string; // optional 2nd line, e.g., "Estate Planning Attorney NYC"
  metaTitle: string;
  metaDescription: string;
  heroEyebrow: string; // small uppercase eyebrow line
  heroLead: string; // 40-70 word lead under name
  photos: AttorneyPhoto[]; // 1+ photo variants
  practiceAreas: string[]; // slugs of practice areas this attorney handles
  credentials: AttorneyCredential[];
  sections: AttorneySection[]; // long-form bio
  quote?: { text: string; attribution?: string };
  sameAs?: string[]; // LinkedIn, Avvo, Justia, Bar profile URLs
  honorific?: 'Esq.' | ''; // for schema name
}

const PLACEHOLDER: AttorneyPhoto = {
  webp: '/img/attorneys/placeholder-monogram.svg',
  fallback: '/img/attorneys/placeholder-monogram.svg',
  alt: 'Morgan Legal Group attorney — photo coming soon',
};

export const attorneys: Record<string, Attorney> = {
  'russel-morgan-esq': {
    slug: 'russel-morgan-esq',
    url: '/russel-morgan-esq/',
    name: 'Russel Morgan, Esq.',
    shortName: 'Russel',
    honorific: 'Esq.',
    title: 'Founder & Principal Attorney',
    titleSubline: 'Estate Planning · Probate · Elder Law',
    metaTitle: 'Russel Morgan, Esq. — Estate Planning Lawyer NYC | Morgan Legal',
    metaDescription:
      'Russel Morgan, Esq. — founder and principal attorney of Morgan Legal Group, P.C. in New York. New York Law School graduate, admitted in New York.',
    heroEyebrow: 'Founding Partner',
    heroLead:
      'Russel Morgan founded Morgan Legal Group to give New York families a single law firm that handles every chapter of their financial life — from drafting a first will, to litigating a contested probate, to defending an elder against fraud. His practice is built on responsiveness, technical command of New York fiduciary law, and a deep personal commitment to his community.',
    photos: [
      { webp: '/img/attorneys/russel-morgan-headshot.webp', fallback: '/img/attorneys/russel-morgan-headshot.jpg', alt: 'Russel Morgan, Esq., Morgan Legal Group founding partner — portrait' },
      { webp: '/img/attorneys/russel-morgan-formal.webp', fallback: '/img/attorneys/russel-morgan-formal.jpg', alt: 'Russel Morgan, Esq., formal portrait' },
      { webp: '/img/attorneys/russel-morgan-standing.webp', fallback: '/img/attorneys/russel-morgan-standing.jpg', alt: 'Russel Morgan, Esq., standing portrait' },
      { webp: '/img/attorneys/russel-morgan-landscape.webp', fallback: '/img/attorneys/russel-morgan-landscape.jpg', alt: 'Russel Morgan, Esq., landscape portrait' },
    ],
    practiceAreas: ['estate-planning', 'probate', 'trusts', 'elder-law', 'asset-protection', 'guardianship'],
    credentials: [
      { type: 'education', value: 'New York Law School — Juris Doctor' },
      { type: 'education', value: "Lloyd's of London — Insurance & Risk" },
      { type: 'admission', value: 'New York State — Admitted to practice' },
      { type: 'membership', value: 'New York State Bar Association' },
      { type: 'language', value: 'English (native)' },
      { type: 'language', value: 'Russian (fluent)' },
    ],
    sections: [
      {
        heading: 'Practice & Approach',
        paragraphs: [
          "Russel Morgan founded Morgan Legal Group with a simple premise: New York families deserve a single trusted firm that can handle the full arc of their financial and family legal needs — estate planning before the crisis, probate during it, and litigation if disputes follow. That premise drives everything from how new clients are screened to how matters are staffed.",
          "Russel personally oversees every estate-planning engagement and every contested Surrogate's Court matter. His estate-planning practice focuses on outcomes, not paperwork: every revocable trust, irrevocable Medicaid asset-protection trust (MAPT), special-needs trust, and durable power of attorney is drafted to perform in the New York legal environment, where common-form templates routinely fail.",
          "His probate and litigation work draws on a deep working knowledge of the Estates, Powers and Trusts Law (EPTL), the Surrogate's Court Procedure Act (SCPA), and the procedural realities of each county's Surrogate's Court — from Manhattan and Brooklyn to Nassau and Suffolk.",
        ],
      },
      {
        heading: 'Education & Bar',
        paragraphs: [
          "Russel earned his Juris Doctor from New York Law School and completed specialized coursework at Lloyd's of London on insurance and risk transfer — a foundation that informs his approach to asset protection and fiduciary risk for his clients today.",
          "He is admitted to practice in the State of New York and is a member in good standing of the New York State Bar Association.",
        ],
      },
      {
        heading: 'Community',
        paragraphs: [
          'Russel is fluent in Russian and serves a substantial Russian-speaking client base across Brooklyn, Manhattan, and Queens. Outside the practice, he is active in community mentorship and education initiatives, particularly programs supporting children\'s educational outcomes.',
        ],
      },
    ],
    quote: {
      text: "Estate planning is not a stack of documents. It is a promise that when something goes wrong — incapacity, conflict, loss — your family will have a clear, court-respected path forward.",
      attribution: 'Russel Morgan, Esq.',
    },
    sameAs: [
      'https://www.linkedin.com/in/russelmorgan/',
      'https://www.avvo.com/attorneys/10038-ny-russel-morgan-3974917.html',
    ],
  },

  'russel-morgan-estate-planning-lawyer-in-nyc': {
    slug: 'russel-morgan-estate-planning-lawyer-in-nyc',
    url: '/russel-morgan-estate-planning-lawyer-in-nyc/',
    name: 'Russel Morgan, Esq.',
    shortName: 'Russel',
    honorific: 'Esq.',
    title: 'NYC Estate Planning Lawyer',
    titleSubline: 'Trusts · Wills · Medicaid Planning · Special Needs',
    metaTitle: 'Russel Morgan — Estate Planning Lawyer in NYC | Morgan Legal',
    metaDescription:
      'New York estate planning with Russel Morgan, Esq. — revocable and irrevocable trusts, Medicaid asset-protection trusts, wills, powers of attorney.',
    heroEyebrow: 'Estate Planning Lawyer · NYC',
    heroLead:
      "If estate planning is done well, your family never sees the inside of a Surrogate's courtroom. Russel Morgan builds plans designed for the New York legal environment — where common-form templates routinely fail, Medicaid recovery is aggressive, and county-by-county Surrogate's Court practice varies.",
    photos: [
      { webp: '/img/attorneys/russel-morgan-formal.webp', fallback: '/img/attorneys/russel-morgan-formal.jpg', alt: 'Russel Morgan, Esq., Estate Planning Lawyer NYC' },
      { webp: '/img/attorneys/russel-morgan-headshot.webp', fallback: '/img/attorneys/russel-morgan-headshot.jpg', alt: 'Russel Morgan, Esq., portrait' },
    ],
    practiceAreas: ['estate-planning', 'trusts', 'asset-protection', 'elder-law', 'power-of-attorney'],
    credentials: [
      { type: 'education', value: 'New York Law School — JD' },
      { type: 'education', value: "Lloyd's of London — Insurance & Risk" },
      { type: 'admission', value: 'New York State Bar' },
      { type: 'language', value: 'English, Russian' },
    ],
    sections: [
      {
        heading: 'Why Estate Planning Matters in New York',
        paragraphs: [
          "New York is one of the most demanding probate jurisdictions in the country. A simple oversight in a will — a missing self-proving affidavit, a witness who has moved out of state, or an ambiguous residuary clause — can turn a six-month probate into a three-year contest. A Medicaid plan executed two months too late costs a family the family home.",
          "Russel's estate-planning practice exists to prevent those outcomes. Every engagement starts with a structured family and asset interview, followed by a written plan that lists, in plain English, exactly what each document does and what it does not do.",
        ],
      },
      {
        heading: 'Core Estate Planning Services',
        paragraphs: [
          "Russel personally drafts and reviews every document, supported by Morgan Legal Group's associate team for execution and follow-through. The core menu includes Last Wills and Testaments executed under New York EPTL § 3-2.1 (with self-proving affidavits under SCPA § 1406), revocable living trusts (often paired with a pour-over will), Medicaid asset-protection trusts (MAPTs) with the 60-month look-back planned around, special-needs trusts under EPTL Article 7, durable powers of attorney on the New York statutory short form (GOL § 5-1501B), and health-care proxies under Public Health Law Article 29-C.",
        ],
      },
      {
        heading: 'How the Process Works',
        paragraphs: [
          "Most estate plans take two visits: the first to gather family, asset, and beneficiary information and to discuss your goals; the second to review final documents and execute under attorney supervision. Russel reviews every document before signing. Documents are stored securely with originals returned to the client and digital copies retained by the firm for life.",
        ],
      },
    ],
    sameAs: [
      'https://www.linkedin.com/in/russelmorgan/',
      'https://www.avvo.com/attorneys/10038-ny-russel-morgan-3974917.html',
    ],
  },

  'simge-karaboga-ozdemir': {
    slug: 'simge-karaboga-ozdemir',
    url: '/simge-karaboga-ozdemir/',
    name: 'Simge Karaboga Ozdemir, Esq.',
    shortName: 'Simge',
    honorific: 'Esq.',
    title: 'Associate Attorney',
    titleSubline: 'Estate Administration · Real Estate · Commercial Litigation',
    metaTitle: 'Simge Karaboga Ozdemir, Esq. — Estate Administration | Morgan Legal',
    metaDescription:
      'Simge Karaboga Ozdemir, Esq. — associate attorney at Morgan Legal Group: estate administration, real estate transactions, commercial litigation.',
    heroEyebrow: 'Associate Attorney',
    heroLead:
      "Simge focuses on estate administration matters — drafting petitions, preparing court filings, and communicating with clients through every stage of probate and administration cases. She also supports the firm's real estate transactions, litigation, and international business matters.",
    photos: [
      { webp: '/img/attorneys/simge-karaboga-ozdemir.webp', fallback: '/img/attorneys/simge-karaboga-ozdemir.jpg', alt: 'Simge Karaboga Ozdemir, Esq., Associate Attorney at Morgan Legal Group' },
    ],
    practiceAreas: ['probate', 'estate-planning', 'real-estate'],
    credentials: [
      { type: 'education', value: 'Fordham University School of Law — LL.M., International Business and Trade Law' },
      { type: 'education', value: 'Istanbul University Faculty of Law — Bachelor of Laws' },
      { type: 'recognition', value: 'Judicial Extern, New York Appellate Division, First Department' },
      { type: 'language', value: 'English' },
      { type: 'language', value: 'Turkish' },
    ],
    sections: [
      {
        heading: 'Professional Philosophy & Approach',
        paragraphs: [
          'Simge serves as an Associate Attorney focusing primarily on estate administration matters. She assists in managing and advancing probate and administration cases — drafting petitions, preparing court filings, and communicating with clients — and supports the team in real estate transactions and litigation matters.',
          "Her approach: thorough preparation, clear communication, and a commitment to guiding clients through complex legal processes with confidence and understanding. Her working style blends meticulous attention to detail with a practical, client-centered mindset.",
          'Core values: professional integrity, reliability, and respect for clients and colleagues. She values transparency, diligence, and a problem-solving mindset in every case she handles.',
        ],
      },
      {
        heading: 'Expertise & Practice Areas',
        paragraphs: [
          'Primary practices: Estate Administration, Real Estate Transactions & Litigation, Commercial Litigation.',
          'Specific services: probate and administration petitions, fiduciary support, and litigation support.',
          'Specialized experience: international business law, which lets her navigate cross-border considerations and provide insight into matters involving foreign legal components. She served as Judicial Extern at the New York Appellate Division, First Department.',
        ],
      },
      {
        heading: 'Client Profile',
        paragraphs: [
          'Simge works with individuals, families, and companies seeking guidance in estate matters and complex transactions.',
        ],
      },
      {
        heading: 'Education & Bar Admissions',
        paragraphs: [
          'Simge earned her LL.M. in International Business and Trade Law from Fordham University School of Law, after completing her Bachelor of Laws at Istanbul University Faculty of Law. She is admitted to practice in New York.',
        ],
      },
    ],
    quote: {
      text: 'I am most passionate about helping clients navigate sensitive matters with clarity and care.',
      attribution: 'Simge Karaboga Ozdemir, Esq.',
    },
  },

  'attorney-executive-advisor-at-morgan-legal-group': {
    slug: 'attorney-executive-advisor-at-morgan-legal-group',
    url: '/attorney-executive-advisor-at-morgan-legal-group/',
    name: 'Guy Chirico',
    shortName: 'Guy',
    honorific: '',
    title: 'Attorney & Executive Advisor',
    titleSubline: 'Strategy · Operations · Risk Management',
    metaTitle: 'Guy Chirico — Attorney & Executive Advisor | Morgan Legal',
    metaDescription:
      "Guy Chirico, Esq. — attorney and executive advisor at Morgan Legal Group. JD Albany Law; former Chief Counsel to the NY State Senate Finance Committee.",
    heroEyebrow: 'Attorney & Executive Advisor',
    heroLead:
      "Guy Chirico brings over three decades of experience driving growth, improving efficiency, and mitigating risk for diverse businesses. As a trusted advisor to executive leaders and boards of directors, he combines legal expertise, strategic planning, and operational management.",
    photos: [
      { webp: '/img/attorneys/guy-chirico.webp', fallback: '/img/attorneys/guy-chirico.webp', alt: 'Guy Chirico, Attorney & Executive Advisor at Morgan Legal Group' },
    ],
    practiceAreas: ['asset-protection', 'real-estate'],
    credentials: [
      { type: 'education', value: 'Albany Law School — Juris Doctor' },
      { type: 'education', value: 'Johns Hopkins University — BA, Humanities, Linguistics, American History' },
      { type: 'admission', value: 'New York State Bar' },
      { type: 'membership', value: 'New York State Bar Association' },
      { type: 'recognition', value: 'Former Chief Counsel — Ranking Member, NY State Senate Finance Committee' },
    ],
    sections: [
      {
        heading: 'Professional Background',
        paragraphs: [
          'Guy Chirico is a seasoned attorney and executive advisor with over three decades of experience driving growth, improving efficiency, and mitigating risk for diverse businesses. As a trusted advisor to executive leaders and boards of directors, Guy brings a unique blend of legal expertise, strategic planning, and operational management skills to Morgan Legal Group.',
        ],
      },
      {
        heading: 'Education',
        paragraphs: [
          'Guy earned his Juris Doctor in Law from Albany Law School and a Bachelor of Arts in Humanities, Linguistics, and American History from Johns Hopkins University. He has honed his skills through various roles, including Chief Counsel to the Ranking Member of the New York State Senate Finance Committee, Business Consultant and Attorney, and Consulting General Manager for a five-star hotel.',
        ],
      },
      {
        heading: 'Industry Experience',
        paragraphs: [
          "Guy's extensive experience spans multiple industries — hospitality, travel, import/export, and non-profit. He has saved a significant regional employer as a court-appointed receiver, implemented process improvements between sales and operations departments, sourced and managed international suppliers, pioneered the import of fine hemp products from Hmong Hill Tribes, negotiated partnerships between Thai artists and prominent businesses, and led strategic planning and asset management for growth.",
        ],
      },
      {
        heading: 'Areas of Expertise',
        paragraphs: [
          "Executive advisory, coaching, and consulting for executive leaders and boards of directors. Strategic planning and execution for efficiency and growth. Operations, supply chain, and procurement management on a national and global basis. Negotiations with U.S. and international vendors. Restructuring of staffing and compensation models for bottom-line performance.",
        ],
      },
      {
        heading: 'Professional Affiliations',
        paragraphs: [
          "Guy is a member of the New York State Bar Association and has built key relationships with leading bankruptcy trustees, counsels, and judges. His skill set includes contract drafting and negotiation, risk mitigation and management, crisis management, team building and leadership, functional IT requirements, insurance and vendor management, import/export, and international logistics.",
        ],
      },
    ],
  },

  'alan-vaitzman-esq': {
    slug: 'alan-vaitzman-esq',
    url: '/alan-vaitzman-esq/',
    name: 'Alan Vaitzman, Esq.',
    shortName: 'Alan',
    honorific: 'Esq.',
    title: 'Senior Associate Attorney',
    titleSubline: 'Probate & Estate Litigation',
    metaTitle: 'Alan Vaitzman, Esq. — Probate & Estate Litigation | Morgan Legal',
    metaDescription:
      "Alan Vaitzman, Esq., is a Senior Associate Attorney at Morgan Legal Group focusing on probate, estate administration, and Surrogate's Court litigation across New York City.",
    heroEyebrow: 'Senior Associate Attorney',
    heroLead:
      "Alan Vaitzman focuses on contested and uncontested probate matters in New York Surrogate's Court — from accelerated SCPA § 1402 admissions to multi-year will contests under SCPA § 1404 examinations. His work covers every county of the City and the surrounding suburban Surrogate's Courts.",
    photos: [
      { webp: '/img/attorneys/alan-vaitzman.webp', fallback: '/img/attorneys/alan-vaitzman.jpg', alt: 'Alan Vaitzman, Esq. — Senior Associate Attorney, Morgan Legal Group' },
    ],
    practiceAreas: ['probate', 'estate-planning', 'trusts', 'asset-protection'],
    credentials: [
      { type: 'admission', value: 'New York State Bar — Admitted to practice' },
      { type: 'membership', value: 'New York State Bar Association' },
    ],
    sections: [
      {
        heading: 'Practice Focus',
        paragraphs: [
          "Alan's practice centers on probate and estate litigation. He handles uncontested probate petitions, administration proceedings, accountings, claim disputes, kinship hearings, and contested matters including will contests, undue-influence claims, and fiduciary-removal proceedings under SCPA Articles 7, 14, 17, and 22.",
          "He routinely appears in New York County, Kings County, Queens, Bronx, Richmond, Nassau, Suffolk, and Westchester Surrogate's Courts, and is familiar with each county's particular procedural rhythms.",
        ],
      },
      {
        heading: 'Approach',
        paragraphs: [
          "Alan's approach favors early evaluation and aggressive procedural posture: many estate disputes are won or lost in the first six months by the discipline of the SCPA § 1404 examination, kinship investigation, or accounting demand. He prepares every case as if it will be litigated, even when settlement is the most likely outcome.",
        ],
      },
    ],
  },

  'peter-gathings-esq': {
    slug: 'peter-gathings-esq',
    url: '/peter-gathings-esq/',
    name: 'Peter Gathings, Esq.',
    shortName: 'Peter',
    honorific: 'Esq.',
    title: 'Of Counsel',
    titleSubline: 'Personal Injury · Litigation',
    metaTitle: 'Peter Gathings, Esq. — Of Counsel | Morgan Legal Group NYC',
    metaDescription:
      "Peter Gathings, Esq., serves as Of Counsel at Morgan Legal Group focusing on personal injury and complex litigation matters across New York State and federal courts.",
    heroEyebrow: 'Of Counsel',
    heroLead:
      "Peter Gathings supports Morgan Legal Group on personal injury and complex litigation matters. His Of Counsel role brings senior trial judgment to cases against institutional defendants and contested liability matters in both state and federal court.",
    photos: [PLACEHOLDER],
    practiceAreas: ['personal-injury'],
    credentials: [
      { type: 'admission', value: 'New York State Bar — Admitted to practice' },
      { type: 'membership', value: 'New York State Bar Association' },
    ],
    sections: [
      {
        heading: 'Practice Focus',
        paragraphs: [
          "Peter's practice supports the firm's personal injury and litigation caseload — motor vehicle accidents, premises liability, and serious injury claims under New York Insurance Law § 5102, along with contested liability matters in state and federal court.",
        ],
      },
      {
        heading: 'Approach',
        paragraphs: [
          "Of Counsel work in litigation requires a particular discipline: knowing when a case must be tried, when it should settle, and when motion practice can change the trajectory. Peter brings senior judgment to those inflection points on every matter where he is engaged.",
        ],
      },
    ],
  },

  'sofia-cristando-esq': {
    slug: 'sofia-cristando-esq',
    url: '/sofia-cristando-esq/',
    name: 'Sofia Cristando, Esq.',
    shortName: 'Sofia',
    honorific: 'Esq.',
    title: 'Associate Attorney',
    titleSubline: 'Family Law · Guardianship',
    metaTitle: 'Sofia Cristando, Esq. — Family Law & Guardianship | Morgan Legal',
    metaDescription:
      'Sofia Cristando, Esq. — associate attorney at Morgan Legal Group: family law, Article 81 and 17-A guardianship, matters involving minors.',
    heroEyebrow: 'Associate Attorney',
    heroLead:
      'Sofia Cristando focuses on family law and guardianship — particularly Article 81 Supreme Court guardianships for incapacitated adults and Article 17-A SCPA guardianships for adults with developmental disabilities. She also handles general family law matters in Family Court and Supreme Court.',
    photos: [PLACEHOLDER],
    practiceAreas: ['family-law', 'guardianship', 'matrimonial', 'elder-law'],
    credentials: [
      { type: 'admission', value: 'New York State Bar — Admitted to practice' },
      { type: 'membership', value: 'New York State Bar Association' },
    ],
    sections: [
      {
        heading: 'Practice Focus',
        paragraphs: [
          "Sofia's practice spans family law and guardianship. Family-law matters include divorce, custody, support, paternity, and orders of protection. Guardianship matters include Article 81 of the Mental Hygiene Law for adults who have lost capacity, and Article 17-A of the SCPA for adults with developmental and intellectual disabilities.",
          "She regularly handles court-appointed referee assignments, Mental Hygiene Legal Service coordination, and the day-to-day care decisions that guardianship work requires.",
        ],
      },
      {
        heading: 'Approach',
        paragraphs: [
          "Guardianship work sits at the intersection of family loyalty, medical necessity, and statutory procedure. Sofia approaches each engagement with patience for the family dynamics and rigor on the statutory record — both are required to secure a sustainable order.",
        ],
      },
    ],
  },

  'bianca-olliver-esq': {
    slug: 'bianca-olliver-esq',
    url: '/bianca-olliver-esq/',
    name: 'Bianca Olliver, Esq.',
    shortName: 'Bianca',
    honorific: 'Esq.',
    title: 'Associate',
    titleSubline: 'Real Estate Transactions · Real Estate Litigation · Landlord-Tenant Disputes',
    metaTitle: 'Bianca Olliver, Esq. — Associate | Morgan Legal Group',
    metaDescription:
      'Associate focused on commercial and residential real estate — transactions and litigation. J.D. from Pace University School of Law. Ran her own practi…',
    heroEyebrow: 'Associate',
    heroLead:
      'Associate focused on commercial and residential real estate — transactions and litigation. J.D. from Pace University School of Law. Ran her own practice before joining Morgan Legal Group. Admitted in New York and New Jersey.',
    photos: [{
      webp: '/img/attorneys/bianca-olliver.webp',
      fallback: '/img/attorneys/bianca-olliver.jpg',
      alt: 'Bianca Olliver, Esq. — attorney at Morgan Legal Group',
    }],
    practiceAreas: ['real-estate'],
    credentials: [
      { type: 'education', value: 'J.D., Pace University School of Law' },
      { type: 'admission', value: 'New York State Bar — Admitted to practice' },
      { type: 'admission', value: 'New Jersey State Bar — Admitted to practice' },
    ],
    sections: [
      {
        heading: 'Practice Focus',
        paragraphs: [
          'Bianca Olliver is a highly accomplished associate at Morgan Legal Group with a focus on commercial and residential real estate transactions and litigation matters. Bianca earned a J.D. from Pace University School of Law and is passionate about providing diligent and effective representation.',
        ],
      },
      {
        heading: 'Experience',
        paragraphs: [
          'Prior to joining Morgan Legal Group, Bianca had her own practice, developing sharp transactional and litigation skills and a deep understanding of the legal system. This experience brings a unique perspective when representing clients in complex disputes.',
          'Bianca\'s real estate practice encompasses both transactional and litigation matters, including purchase and sale agreements, lease negotiations, landlord-tenant disputes, partition actions, and title issues. Bianca is admitted to practice in both New York and New Jersey.',
        ],
      },
    ],
  },
  'james-threatt-esq': {
    slug: 'james-threatt-esq',
    url: '/james-threatt-esq/',
    name: 'James Threatt, Esq.',
    shortName: 'James',
    honorific: 'Esq.',
    title: 'Associate',
    titleSubline: 'Estate Planning · Probate · Wills & Trusts',
    metaTitle: 'James Threatt, Esq. — Associate | Morgan Legal Group',
    metaDescription:
      'New York estate planning attorney committed to helping families protect their assets and plan for the future through comprehensive wills, trusts, and …',
    heroEyebrow: 'Associate',
    heroLead:
      'New York estate planning attorney committed to helping families protect their assets and plan for the future through comprehensive wills, trusts, and probate administration.',
    photos: [PLACEHOLDER],
    practiceAreas: ['estate-planning', 'probate', 'wills-and-trusts', 'elder-law'],
    credentials: [
      { type: 'admission', value: 'New York State Bar — Admitted to practice' },
    ],
    sections: [
      {
        heading: 'Practice Focus',
        paragraphs: [
          'James Threat is an estate planning attorney at Morgan Legal Group who is committed to helping New York families protect their assets and plan for the future. His practice focuses on drafting wills and trusts, probate administration, and elder law matters.',
        ],
      },
      {
        heading: 'Experience',
        paragraphs: [
          'James works closely with clients to understand their unique family and financial circumstances, developing customized estate plans that ensure their wishes are honored. He handles everything from simple wills to complex trust structures, and guides executors and administrators through the probate process in New York Surrogate\'s Court.',
          'Known for his thorough approach and clear communication, James makes estate planning accessible and straightforward for every client.',
        ],
      },
    ],
  },
  'allan-louis-esq': {
    slug: 'allan-louis-esq',
    url: '/allan-louis-esq/',
    name: 'Allan Louis, Esq.',
    shortName: 'Allan',
    honorific: 'Esq.',
    title: 'Associate',
    titleSubline: 'Real Estate · Business Law · Contract Disputes',
    metaTitle: 'Allan Louis, Esq. — Associate | Morgan Legal Group',
    metaDescription:
      'Business and real estate attorney focused on commercial transactions, corporate formations, and contract disputes across New York City.',
    heroEyebrow: 'Associate',
    heroLead:
      'Business and real estate attorney focused on commercial transactions, corporate formations, and contract disputes across New York City.',
    photos: [PLACEHOLDER],
    practiceAreas: ['real-estate'],
    credentials: [
      { type: 'admission', value: 'New York State Bar — Admitted to practice' },
    ],
    sections: [
      {
        heading: 'Practice Focus',
        paragraphs: [
          'Allan Louis is a business and real estate attorney at Morgan Legal Group with a focus on commercial transactions, corporate formations, and contract disputes. He represents entrepreneurs, landlords, and business owners across all five boroughs of New York City.',
        ],
      },
      {
        heading: 'Experience',
        paragraphs: [
          'Allan\'s practice includes drafting and reviewing commercial leases, purchase agreements, and partnership documents. He advises on LLC and corporation formations, business succession planning, and contract negotiations. His real estate work spans residential and commercial closings, title issues, and landlord-tenant matters.',
          'Allan brings a practical, business-minded approach to legal counsel — helping clients make informed decisions that protect their interests and support their long-term goals.',
        ],
      },
    ],
  },
  'jacqueline-bohatch-esq': {
    slug: 'jacqueline-bohatch-esq',
    url: '/jacqueline-bohatch-esq/',
    name: 'Jacqueline Bohatch, Esq.',
    shortName: 'Jacqueline',
    honorific: 'Esq.',
    title: 'Associate',
    titleSubline: 'Estate Planning · Guardianship · Elder Law',
    metaTitle: 'Jacqueline Bohatch, Esq. — Associate | Morgan Legal Group',
    metaDescription:
      'Estate planning and elder law attorney guiding New York families through guardianship proceedings, Medicaid planning, and comprehensive estate strategies.',
    heroEyebrow: 'Associate',
    heroLead:
      'Estate planning and elder law attorney guiding New York families through guardianship proceedings, Medicaid planning, and comprehensive estate strategies.',
    photos: [PLACEHOLDER],
    practiceAreas: ['estate-planning', 'guardianship', 'elder-law', 'medicaid-planning'],
    credentials: [
      { type: 'admission', value: 'New York State Bar — Admitted to practice' },
    ],
    sections: [
      {
        heading: 'Practice Focus',
        paragraphs: [
          'Jacqueline Bohatch is an estate planning and elder law attorney at Morgan Legal Group who guides New York families through guardianship proceedings, Medicaid planning, and comprehensive estate strategies.',
        ],
      },
      {
        heading: 'Experience',
        paragraphs: [
          'Jacqueline\'s practice focuses on helping families protect aging loved ones through Article 81 guardianship proceedings, Medicaid asset protection trusts, and long-term care planning. She also handles estate planning fundamentals — wills, trusts, powers of attorney, and healthcare proxies.',
          'Known for her empathetic approach and meticulous attention to detail, Jacqueline takes the time to understand each family\'s unique circumstances before developing a plan. She is committed to making complex elder law and estate planning processes as clear and stress-free as possible.',
        ],
      },
    ],
  },
  'shaniece-hunter-esq': {
    slug: 'shaniece-hunter-esq',
    url: '/shaniece-hunter-esq/',
    name: 'Shaniece Hunter, Esq.',
    shortName: 'Shaniece',
    honorific: 'Esq.',
    title: 'Associate',
    titleSubline: 'Family Law · Personal Injury · Immigration',
    metaTitle: 'Shaniece Hunter, Esq. — Associate | Morgan Legal Group',
    metaDescription:
      'Versatile litigator handling family law, personal injury, and immigration matters. Advocates fiercely for clients\' rights in court and negotiation.',
    heroEyebrow: 'Associate',
    heroLead:
      'Versatile litigator handling family law, personal injury, and immigration matters. Advocates fiercely for clients\' rights in court and negotiation.',
    photos: [PLACEHOLDER],
    practiceAreas: ['family-law', 'personal-injury', 'matrimonial'],
    credentials: [
      { type: 'admission', value: 'New York State Bar — Admitted to practice' },
    ],
    sections: [
      {
        heading: 'Practice Focus',
        paragraphs: [
          'Shanice Hunter is a versatile litigator at Morgan Legal Group handling family law, personal injury, and immigration matters. She advocates fiercely for her clients\' rights in both courtroom proceedings and settlement negotiations.',
        ],
      },
      {
        heading: 'Experience',
        paragraphs: [
          'Shanice\'s family law practice covers divorce, child custody, child support, and orders of protection. In personal injury, she represents clients injured in car accidents, slip and falls, and premises liability incidents. Her immigration work includes visa applications, green card petitions, and deportation defense.',
          'Shanice brings energy, dedication, and a genuine commitment to justice to every case. She believes every client deserves aggressive, compassionate representation regardless of the complexity of their matter.',
        ],
      },
    ],
  },
  'sabrina-vancamp-esq': {
    slug: 'sabrina-vancamp-esq',
    url: '/sabrina-vancamp-esq/',
    name: 'Sabrina VanCamp, Esq.',
    shortName: 'Sabrina',
    honorific: 'Esq.',
    title: 'Associate',
    titleSubline: 'Probate · Trust Administration · Estate Litigation',
    metaTitle: 'Sabrina VanCamp, Esq. — Associate | Morgan Legal Group',
    metaDescription:
      'Probate and trust administration attorney helping executors, trustees, and beneficiaries through complex estate proceedings in New York Surrogate\'s Court.',
    heroEyebrow: 'Associate',
    heroLead:
      'Probate and trust administration attorney helping executors, trustees, and beneficiaries through complex estate proceedings in New York Surrogate\'s Court.',
    photos: [PLACEHOLDER],
    practiceAreas: ['probate', 'trusts', 'estate-planning'],
    credentials: [
      { type: 'admission', value: 'New York State Bar — Admitted to practice' },
    ],
    sections: [
      {
        heading: 'Practice Focus',
        paragraphs: [
          'Sabrina VanCamp is a probate and trust administration attorney at Morgan Legal Group who helps executors, trustees, and beneficiaries navigate complex estate proceedings in New York Surrogate\'s Court.',
        ],
      },
      {
        heading: 'Experience',
        paragraphs: [
          'Sabrina\'s practice includes probate filings, estate accounting, trust administration, trustee removal proceedings, and fiduciary misconduct litigation. She represents both fiduciaries fulfilling their duties and beneficiaries seeking accountability.',
          'Meticulous with court filings and fiduciary obligations, Sabrina ensures that every estate is administered properly, efficiently, and in accordance with New York law. She is known for her organized approach and clear communication with clients during what is often a difficult and emotional process.',
        ],
      },
    ],
  },
  'gabriella-vaitzman-esq': {
    slug: 'gabriella-vaitzman-esq',
    url: '/gabriella-vaitzman-esq/',
    name: 'Gabriella Vaitzman, Esq.',
    shortName: 'Gabriella',
    honorific: 'Esq.',
    title: 'Associate — Florida',
    titleSubline: 'Estate Planning · Probate · Real Estate',
    metaTitle: 'Gabriella Vaitzman, Esq. — Associate — Florida | Morgan Legal Group',
    metaDescription:
      'Florida-licensed estate planning and probate attorney. Helps Florida families and snowbirds with wills, trusts, real estate transactions, and business…',
    heroEyebrow: 'Associate — Florida',
    heroLead:
      'Florida-licensed estate planning and probate attorney. Helps Florida families and snowbirds with wills, trusts, real estate transactions, and business formations.',
    photos: [{
      webp: '/img/attorneys/gabriella-vaitzman.webp',
      fallback: '/img/attorneys/gabriella-vaitzman.jpg',
      alt: 'Gabriella Vaitzman, Esq. — attorney at Morgan Legal Group',
    }],
    practiceAreas: ['estate-planning', 'probate', 'real-estate'],
    credentials: [
      { type: 'admission', value: 'Florida State Bar — Admitted to practice' },
    ],
    sections: [
      {
        heading: 'Practice Focus',
        paragraphs: [
          'Gabriella Vaitzman is a Florida-licensed estate planning and probate attorney at Morgan Legal Group. She helps Florida families and snowbirds with wills, trusts, real estate transactions, and business formations.',
        ],
      },
      {
        heading: 'Experience',
        paragraphs: [
          'Gabriella provides seamless coordination between our New York and Florida offices, ensuring that clients with assets in both states receive cohesive legal representation. Her practice covers Florida probate administration, trust planning, residential and commercial real estate closings, and business entity formations.',
          'She is committed to providing personalized, hands-on service to every client — understanding that estate planning and real estate transactions are among the most important legal decisions families make.',
        ],
      },
    ],
  },
  'liza-gross-esq': {
    slug: 'liza-gross-esq',
    url: '/liza-gross-esq/',
    name: 'Liza Gross, Esq.',
    shortName: 'Liza',
    honorific: 'Esq.',
    title: 'Associate',
    titleSubline: 'Real Estate · Commercial Litigation · Asset Protection',
    metaTitle: 'Liza Gross, Esq. — Associate | Morgan Legal Group',
    metaDescription:
      'Handles real estate matters, commercial litigation, and asset protection at Morgan Legal Group in New York City.',
    heroEyebrow: 'Associate',
    heroLead:
      'Handles real estate matters, commercial litigation, and asset protection at Morgan Legal Group in New York City.',
    photos: [PLACEHOLDER],
    practiceAreas: ['real-estate', 'asset-protection'],
    credentials: [
      { type: 'admission', value: 'New York State Bar — Admitted to practice' },
    ],
    sections: [
      {
        heading: 'Practice Focus',
        paragraphs: [
          'Liza Gross is an associate at Morgan Legal Group, P.C. in New York City. Her practice covers real estate, commercial litigation, and asset protection.',
        ],
      },
    ],
  },
  'charles-marchese-esq': {
    slug: 'charles-marchese-esq',
    url: '/charles-marchese-esq/',
    name: 'Charles Marchese, Esq.',
    shortName: 'Charles',
    honorific: 'Esq.',
    title: 'Of Counsel',
    metaTitle: 'Charles Marchese, Esq. — Of Counsel | Morgan Legal Group',
    metaDescription:
      'Charles Marchese, Esq. — Of Counsel to Morgan Legal Group in New York City, admitted to practice before the New York State Bar.',
    heroEyebrow: 'Of Counsel',
    heroLead:
      'Charles Marchese serves as Of Counsel to Morgan Legal Group, P.C. in New York City. He is admitted to practice before the New York State Bar.',
    photos: [PLACEHOLDER],
    practiceAreas: [],
    credentials: [
      { type: 'admission', value: 'New York State Bar — Admitted to practice' },
    ],
    sections: [
      {
        heading: 'Practice Focus',
        paragraphs: [
          'Charles Marchese serves as Of Counsel to Morgan Legal Group, P.C. in New York City.',
        ],
      },
    ],
  },
};

export const attorneyOrder: string[] = [
  'russel-morgan-esq',
  'simge-karaboga-ozdemir',
  'attorney-executive-advisor-at-morgan-legal-group',
  'alan-vaitzman-esq',
  'peter-gathings-esq',
  'sofia-cristando-esq',
  'russel-morgan-estate-planning-lawyer-in-nyc',
  'bianca-olliver-esq',
  'james-threatt-esq',
  'jacqueline-bohatch-esq',
  'sabrina-vancamp-esq',
  'allan-louis-esq',
  'shaniece-hunter-esq',
  'liza-gross-esq',
  'gabriella-vaitzman-esq',
  'charles-marchese-esq',
];
